import type { DscWorkbenchDashboardResponseV2 } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { dscWorkbenchV2Api } from "../../../api/client";
import { dscWorkbenchKeys } from "./dscWorkbenchKeys";

/**
 * Shared queryOptions for a deal's DSC Workbench dashboard (key + queryFn).
 * Backed by the generated WorkbenchDashboardV2Api.getWorkbenchV2, which
 * returns every Step 6 workbench section in one composite response (driver,
 * secondSigner, vehicle, financials, …) plus each applicant's current-state
 * validations and the current stipulation panel. Partial failures degrade
 * gracefully — failed sections are omitted and surfaced as warnings in
 * `metadata`, so consumers should read each section defensively rather than
 * assume the whole payload succeeded.
 *
 * Reusable by useQuery, prefetchQuery, ensureQueryData, getQueryData, and
 * setQueryData.
 * @param {string} approvalId - The deal / approval identifier.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function dscWorkbenchQuery(approvalId: string) {
  return queryOptions({
    queryKey: dscWorkbenchKeys.detail({ approvalId }),
    queryFn: async ({ signal }): Promise<DscWorkbenchDashboardResponseV2> => {
      const response = await dscWorkbenchV2Api.getWorkbenchV2(approvalId, { signal });
      return response.data;
    },
    staleTime: 30_000,
    enabled: !!approvalId,
  });
}

/**
 * Reads the DSC Workbench dashboard for a deal. Disabled (never fires) until
 * the approval id is present, so callers can render an empty/loading state
 * instead of issuing a doomed `/applications//…` request.
 * @param {string} approvalId - The deal / approval identifier.
 * @returns {ReturnType<typeof useQuery<DscWorkbenchDashboardResponseV2>>} The TanStack Query result.
 */
export function useDscWorkbench(approvalId: string) {
  return useQuery(dscWorkbenchQuery(approvalId));
}
